import type { ApprovalStatus, Vendor } from '@shopgnd/shared';
import { isRealEstateVendor } from '@/lib/realEstateVendor';

export function vendorPropertyListingApprovalStatus(
  vendor: Pick<Vendor, 'propertyListingApprovalStatus'>,
): ApprovalStatus {
  return vendor.propertyListingApprovalStatus ?? 'none';
}

/**
 * Real-estate vendors approved on the main store queue before property-agency
 * approval existed are treated as approved when propertyListingApprovalStatus is still none.
 */
export function vendorHasPropertyListingAccess(
  vendor: Pick<
    Vendor,
    'propertyListingApprovalStatus' | 'approvalStatus' | 'businessType' | 'businessModes'
  >,
): boolean {
  if (!isRealEstateVendor(vendor)) return false;
  const propertyStatus = vendorPropertyListingApprovalStatus(vendor);
  if (propertyStatus === 'approved') return true;
  if (propertyStatus === 'rejected') return false;
  const storeApproved = (vendor.approvalStatus ?? 'none') === 'approved';
  // Pending/none after store approval: store review already passed; do not block publish again.
  if (propertyStatus === 'pending' || propertyStatus === 'none') return storeApproved;
  return false;
}

export function vendorCanPublishPropertyListings(vendor: Vendor): boolean {
  return vendorHasPropertyListingAccess(vendor);
}

/** Status to set when a vendor completes property-sector onboarding. */
export function propertyListingApprovalAfterOnboarding(
  propertyOnboarding: boolean,
  storeApprovalStatus: ApprovalStatus | undefined,
): ApprovalStatus | undefined {
  if (!propertyOnboarding) return undefined;
  return (storeApprovalStatus ?? 'none') === 'approved' ? 'approved' : 'pending';
}

export type PropertyListingHubState =
  | 'not-agency'
  | 'blocked'
  | 'admin-preview'
  | 'admin-act-as'
  | 'ready';

/** Single source of truth for property hub + post page (avoids impersonation UI/API mismatch). */
export function resolvePropertyListingHubState(
  vendor: Vendor | null | undefined,
  options?: { adminImpersonating?: boolean },
): PropertyListingHubState {
  if (!vendor || !isRealEstateVendor(vendor)) return 'not-agency';
  const canPublish = vendorCanPublishPropertyListings(vendor);
  if (options?.adminImpersonating) {
    return canPublish ? 'admin-act-as' : 'admin-preview';
  }
  if (canPublish) return 'ready';
  return 'blocked';
}

/** Hub / wizard UI — true when seller can use property tools (not admin preview-only). */
export function canAccessPropertyListingTools(
  vendor: Vendor | null | undefined,
  options?: { adminImpersonating?: boolean },
): boolean {
  const state = resolvePropertyListingHubState(vendor, options);
  return (
    state === 'ready' ||
    state === 'admin-preview' ||
    state === 'admin-act-as'
  );
}

export function canPublishPropertyListingsInHub(
  vendor: Vendor | null | undefined,
  options?: { adminImpersonating?: boolean },
): boolean {
  const state = resolvePropertyListingHubState(vendor, options);
  return state === 'ready' || state === 'admin-act-as';
}

export function propertyListingApprovalLabel(
  status: ApprovalStatus,
  vendor?: Pick<Vendor, 'approvalStatus'>,
): string {
  switch (status) {
    case 'pending':
      return 'Your property agency application is under review. You can browse ShopGND while we verify your license.';
    case 'rejected':
      return 'Property listing access was not approved. Update your agency details or contact support.';
    case 'approved':
      return 'Property listing access is approved.';
    default:
      if ((vendor?.approvalStatus ?? 'none') === 'approved') {
        return 'Your store is approved. Refresh this page to load property tools, or contact support if listings stay locked.';
      }
      return 'Request property listing access from your agency profile or complete real-estate onboarding.';
  }
}
